import type { ReactNode } from "react";
import { useTranslations } from "use-intl";

import { FormatIcon } from "@/components/format-icon";
import type { LinkTarget } from "@/components/link";
import {
  TOGGLE_SEGMENT_TRACK,
  ToggleGroupOptionItem,
  ToggleGroupRoot,
} from "@/components/ui/toggle-group";
import { useFormatNumber } from "@/hooks/use-format-number";
import { cn } from "@/lib/utils";
import type { FormatCategoryWithOther as FormatCategory } from "@garimpa/lib/formatGroups";

export interface FormatFilterOption {
  value: string;
  /** Defaults to the `shared.releaseFilters` label for the format category. */
  label?: string;
  count?: number;
  /** Shown instead of the count (e.g. "Out of stock" or a price). */
  badge?: ReactNode;
}

interface FormatFilterControlProps {
  options: FormatFilterOption[];
  /** Selected format, or `"all"`. */
  value: string;
  onChange?: (value: string) => void;
  /** Renders each segment as a link (crawlable filters); navigation owns the change. */
  linkTarget?: (value: string) => LinkTarget;
  /**
   * The leading "All" segment. Omit its `count` when the per-format counts overlap
   * (e.g. people with several formats); pass `false` for switches without "All".
   */
  all?: { count?: number } | false;
  className?: string;
  wrapperClassName?: string;
  "aria-label"?: string;
}

/**
 * The format filter used on every catalog surface: a segmented control with the
 * format icon, label and result count, scrolling horizontally on narrow screens.
 * Every segment takes the width of the widest one.
 * Returns an empty `<span />` when there is nothing to choose so compact bars can
 * collapse the row.
 */
export function FormatFilterControl({
  options,
  value,
  onChange,
  linkTarget,
  all = {},
  className,
  wrapperClassName,
  "aria-label": ariaLabel,
}: FormatFilterControlProps) {
  const t = useTranslations("shared.releaseFilters");
  const formatNumber = useFormatNumber();
  if (options.length === 0) return <span />;

  const segments: FormatFilterOption[] = all
    ? [{ value: "all", label: t("allFormats"), ...all }, ...options]
    : options;

  return (
    <ToggleGroupRoot
      value={[value]}
      onValueChange={(values) => {
        const next = values[0];
        if (next) onChange?.(next);
        else if (all) onChange?.("all");
      }}
      scrollHint
      aria-label={ariaLabel}
      wrapperClassName={wrapperClassName}
      className={cn(TOGGLE_SEGMENT_TRACK, "grid w-max auto-cols-fr grid-flow-col p-0.5", className)}
    >
      {segments.map((option) => (
        <ToggleGroupOptionItem
          key={option.value}
          value={option.value}
          variant="segment"
          badge={option.badge ?? (option.count != null ? formatNumber(option.count) : undefined)}
          link={linkTarget?.(option.value)}
        >
          <FormatIcon format={option.value as FormatCategory | "all"} size={16} />
          <span>{option.label ?? (t.has(option.value) ? t(option.value) : option.value)}</span>
        </ToggleGroupOptionItem>
      ))}
    </ToggleGroupRoot>
  );
}
